import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import PsychologyIcon from "@mui/icons-material/Psychology";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import RefreshIcon from "@mui/icons-material/Refresh";
import EncounterTimer from "./EncounterTimer";

const PROGRAM_OPTIONS = ["CCM", "RPM", "BHI", "PCM"];
const ENCOUNTER_MODES = [
  { value: "phone", label: "Phone" },
  { value: "video", label: "Video" },
  { value: "portal", label: "Portal" },
];
const INTERVENTION_OPTIONS = [
  "Medication reconciliation",
  "Device troubleshooting",
  "Symptom review",
  "Goal coaching",
  "Care plan update",
  "Escalated to provider",
  "Coordinated referrals",
];
const RESOURCE_OPTIONS = [
  "Transportation",
  "Food assistance",
  "Medication delivery",
  "Financial counseling",
  "Behavioral health support",
  "Community program",
];
const MOOD_OPTIONS = ["stable", "motivated", "anxious", "frustrated", "declined to share"];

const DEFAULT_FORM = {
  program_types: ["CCM"],
  program_minutes: {},
  encounter_mode: "phone",
  encounter_focus: "",
  summary: "",
  notes: "",
  interventions: [],
  next_steps_text: "",
  coordinator_next_steps_text: "",
  follow_up_at: "",
  patient_mood: "stable",
  escalation_required: false,
  vitals_reviewed: false,
  medications_reviewed: false,
  barriers_reviewed: false,
  resources_offered: [],
};

const formatDateTimeForInput = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
};

const parseNextSteps = (text) =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

function EncounterDrawer({
  open,
  onClose,
  patient,
  session,
  apiBase,
  loadingSession = false,
  onSessionChange,
  onShowSnackbar,
  onEncounterCompleted,
  agendaData,
  chartActivityLog = [],
  callElapsedSeconds = 0,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [formState, setFormState] = useState(DEFAULT_FORM);
  const [durationSeconds, setDurationSeconds] = useState(session?.duration_seconds || 0);
  const [timerSeed, setTimerSeed] = useState(session?.duration_seconds || 0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const sessionIdRef = useRef(null);
  const processedActivityCount = useRef(0);

  // Auto-append new chart activity entries to the notes field
  useEffect(() => {
    if (chartActivityLog.length > processedActivityCount.current) {
      const newEntries = chartActivityLog.slice(processedActivityCount.current);
      processedActivityCount.current = chartActivityLog.length;
      setFormState((prev) => {
        const existing = prev.notes || "";
        const activityLines = newEntries.map((e) => `✓ ${e.text}`).join("\n");
        const separator = existing ? "\n" : "";
        return { ...prev, notes: existing + separator + activityLines };
      });
    }
  }, [chartActivityLog]);

  useEffect(() => {
    if (!open && sessionIdRef.current) {
      // Auto-save when drawer closes but DON'T clear the session ref
      if (session && session.status !== "completed") {
        handleSave();
      }
      // DO NOT RESET sessionIdRef - we want to keep the session active
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!session) return;
    const isNewSession = sessionIdRef.current !== session.id;
    // Only hydrate form from server on NEW session — not on every save/patch
    // This prevents checkbox/intervention state from being overwritten
    if (isNewSession) {
      setFormState({
        program_types: session.program_type ? session.program_type.split(",").map(p => p.trim()).filter(Boolean) : DEFAULT_FORM.program_types,
        encounter_mode: session.encounter_mode || DEFAULT_FORM.encounter_mode,
        encounter_focus: session.encounter_focus || "",
        summary: session.summary || "",
        notes: session.notes || "",
        interventions: session.interventions || [],
        next_steps_text: (session.next_steps || []).join("\n"),
        coordinator_next_steps_text: (session.coordinator_next_steps || []).join("\n"),
        follow_up_at: formatDateTimeForInput(session.follow_up_at),
        patient_mood: session.patient_mood || DEFAULT_FORM.patient_mood,
        escalation_required: Boolean(session.escalation_required),
        vitals_reviewed: Boolean(session.vitals_reviewed),
        medications_reviewed: Boolean(session.medications_reviewed),
        barriers_reviewed: Boolean(session.barriers_reviewed),
        resources_offered: session.resources_offered || [],
        program_minutes: (session.metadata || {}).program_minutes || {},
      });
      const startingSeconds = session.duration_seconds || 0;
      setDurationSeconds(startingSeconds);
      setTimerSeed(startingSeconds);
      // Restore timer running state from metadata
      const metadata = session.metadata || {};
      const shouldRun = metadata.timer_running || session.status === "in_progress";
      setTimerRunning(shouldRun);
      sessionIdRef.current = session.id;
      processedActivityCount.current = 0; // Reset so new session picks up any existing activity
    }
  }, [session]);

  const statusColor = useMemo(() => {
    switch (session?.status) {
      case "completed":
        return "success";
      case "in_progress":
        return "secondary";
      case "paused":
        return "warning";
      default:
        return "default";
    }
  }, [session]);

  const handleFieldChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const toggleChipValue = (field, value) => {
    setFormState((prev) => {
      const current = new Set(prev[field]);
      if (current.has(value)) {
        current.delete(value);
      } else {
        current.add(value);
      }
      return { ...prev, [field]: Array.from(current) };
    });
  };

  // The chart's call clock is the source of truth while the chart is open;
  // keep any longer duration already saved on a resumed session.
  const effectiveDuration = Math.max(durationSeconds || 0, callElapsedSeconds || 0);

  const buildPayload = useCallback(
    (statusOverride) => {
      if (!session) return null;
      return {
        program_type: formState.program_types.join(","),
        encounter_mode: formState.encounter_mode,
        encounter_focus: formState.encounter_focus,
        summary: formState.summary,
        notes: formState.notes,
        interventions: formState.interventions,
        next_steps: parseNextSteps(formState.next_steps_text),
        coordinator_next_steps: parseNextSteps(formState.coordinator_next_steps_text),
        follow_up_at: formState.follow_up_at ? new Date(formState.follow_up_at).toISOString() : null,
        patient_mood: formState.patient_mood,
        escalation_required: formState.escalation_required,
        vitals_reviewed: formState.vitals_reviewed,
        duration_seconds: effectiveDuration,
        medications_reviewed: formState.medications_reviewed,
        barriers_reviewed: formState.barriers_reviewed,
        resources_offered: formState.resources_offered,
        metadata: {
          timer_running: timerRunning,
          program_minutes: formState.program_minutes || {},
        },
        status: statusOverride || (timerRunning ? "in_progress" : session.status),
      };
    },
    [effectiveDuration, formState, session, timerRunning]
  );

  const handleSave = async (statusOverride) => {
    if (!session) return;
    const payload = buildPayload(statusOverride);
    if (!payload) return;
    setSaving(true);
    try {
      const response = await fetch(`${apiBase}/encounter-sessions/${session.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Failed to save session: ${response.status}`);
      }
      const updatedSession = await response.json();
      onSessionChange?.(updatedSession);
      onShowSnackbar?.({
        open: true,
        message: "Encounter draft saved",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      onShowSnackbar?.({
        open: true,
        message: "Unable to save encounter",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!session || !formState.summary) {
      onShowSnackbar?.({
        open: true,
        message: "Add a quick summary before completing the encounter",
        severity: "warning",
      });
      return;
    }
    const payload = {
      summary: formState.summary,
      notes: formState.notes,
      interventions: formState.interventions,
      next_steps: parseNextSteps(formState.next_steps_text),
      coordinator_next_steps: parseNextSteps(formState.coordinator_next_steps_text),
      follow_up_at: formState.follow_up_at ? new Date(formState.follow_up_at).toISOString() : null,
      duration_seconds: effectiveDuration,
      metadata: { program_minutes: formState.program_minutes || {} },
      create_encounter_entry: true,
      encounter_type: `${formState.program_types.join("/")} ${formState.encounter_mode === "phone" ? "Phone" : "Visit"}`,
      chief_complaint: formState.encounter_focus || "Care management outreach",
      treatment_plan: formState.notes || formState.summary,
      provider: session.created_by || "Care Coordinator",
    };

    setSaving(true);
    try {
      const response = await fetch(`${apiBase}/encounter-sessions/${session.id}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Failed to complete encounter: ${response.status}`);
      }
      const result = await response.json();
      onEncounterCompleted?.(result);
      onShowSnackbar?.({
        open: true,
        message: "Encounter completed",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      onShowSnackbar?.({
        open: true,
        message: "Unable to complete encounter",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const drawerWidth = useMemo(() => ({ xs: "100%", sm: "100%", md: 380, lg: 420, xl: 460 }), []);

  const buildGeneratePayload = (mode) => ({
    mode,
    chart_activity: chartActivityLog.map((e) => e.text),
    program_type: formState.program_types.join(","),
    encounter_mode: formState.encounter_mode,
    encounter_focus: formState.encounter_focus,
    interventions: formState.interventions,
    vitals_reviewed: formState.vitals_reviewed,
    medications_reviewed: formState.medications_reviewed,
    barriers_reviewed: formState.barriers_reviewed,
    patient_mood: formState.patient_mood,
    resources_offered: formState.resources_offered,
    existing_summary: formState.summary,
    existing_notes: formState.notes,
  });

  const handleGenerateDocumentation = async (mode = "summary") => {
    if (!session) return;
    setGenerating(true);
    try {
      const response = await fetch(
        `${apiBase}/encounter-sessions/${session.id}/generate-documentation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildGeneratePayload(mode)),
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to generate documentation: ${response.status}`);
      }
      const result = await response.json();
      if (result.success && result.documentation) {
        const doc = result.documentation;
        setFormState((prev) => ({
          ...prev,
          summary: doc.summary || prev.summary,
          // Refresh (update) appends to existing notes; Generate replaces
          notes: mode === "update"
            ? (prev.notes ? prev.notes + "\n\n" + doc.notes : doc.notes) || prev.notes
            : doc.notes || prev.notes,
          next_steps_text: (doc.next_steps || []).join("\n") || prev.next_steps_text,
          coordinator_next_steps_text: (doc.coordinator_next_steps || []).join("\n") || prev.coordinator_next_steps_text,
        }));
        onShowSnackbar?.({
          open: true,
          message: result.ai_fallback
            ? "Documentation generated (template — AI temporarily unavailable)"
            : mode === "update"
              ? "✓ Notes updated with session activity — review before completing"
              : "✨ Patient summary generated — review and edit before completing",
          severity: "success",
        });
      }
    } catch (error) {
      console.error(error);
      onShowSnackbar?.({
        open: true,
        message: "Unable to generate AI documentation",
        severity: "error",
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      hideBackdrop={true}
      role="dialog"
      aria-labelledby="encounter-drawer-title"
      ModalProps={{
        keepMounted: true, // Keep mounted so timer doesn't unmount
      }}
      SlideProps={{
        unmountOnExit: false, // NEVER unmount - keep timer alive
      }}
      PaperProps={{
        sx: {
          width: drawerWidth,
          background: isDark
            ? "linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.98) 100%)"
            : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          backdropFilter: isDark ? "blur(24px) saturate(180%)" : "none",
          WebkitBackdropFilter: isDark ? "blur(24px) saturate(180%)" : "none",
          borderLeft: isDark ? "1px solid rgba(148,163,184,0.1)" : "1px solid rgba(0,0,0,0.12)",
          p: 4,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(0,0,0,0.1)",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(148,163,184,0.3)",
            borderRadius: "4px",
            "&:hover": {
              background: "rgba(148,163,184,0.5)",
            },
          },
        },
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography
            id="encounter-drawer-title"
            component="h2"
            variant="overline"
            color="text.secondary"
            display="block"
          >
            Encounter
          </Typography>
          <Typography variant="h6" component="p">{patient?.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            MRN {patient?.mrn} • {patient?.phone}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {agendaData && agendaData.total_items > 0 && (
            <Tooltip title="Agenda items addressed this encounter">
              <Chip
                icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                label={`${agendaData.addressed || 0}/${agendaData.total_items} agenda`}
                size="small"
                color={
                  (agendaData.addressed || 0) >= agendaData.total_items
                    ? "success"
                    : (agendaData.addressed || 0) > 0
                    ? "warning"
                    : "default"
                }
                variant="outlined"
                sx={{ fontSize: 11, height: 22 }}
              />
            </Tooltip>
          )}
          {session && (
            <Chip
              label={(session.status || "draft").replace("_", " ")}
              color={statusColor}
              size="small"
              variant="outlined"
              sx={{ textTransform: "capitalize" }}
            />
          )}
          <IconButton onClick={onClose} aria-label="Close encounter drawer">
            <CloseIcon aria-label="Close encounter drawer" />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {loadingSession || !session ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Preparing encounter workspace…
          </Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" height="100%" gap={3}>
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              border: "1px solid rgba(255,255,255,0.08)",
              backgroundColor: "rgba(255,255,255,0.02)",
            }}
          >
            <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
              {PROGRAM_OPTIONS.map((program) => {
                const selected = formState.program_types.includes(program);
                return (
                  <Chip
                    key={program}
                    label={program}
                    size="small"
                    color={selected ? "secondary" : "default"}
                    variant={selected ? "filled" : "outlined"}
                    onClick={() =>
                      setFormState((prev) => {
                        const current = new Set(prev.program_types);
                        if (current.has(program)) {
                          current.delete(program);
                          // Keep at least one program selected
                          if (current.size === 0) return prev;
                        } else {
                          current.add(program);
                        }
                        return { ...prev, program_types: Array.from(current) };
                      })
                    }
                    data-program-type={program}
                    aria-label={`Toggle ${program} program`}
                    aria-pressed={selected}
                  />
                );
              })}
            </Stack>
            <FormControl fullWidth size="small">
              <InputLabel id="encounter-mode-label">Encounter Mode</InputLabel>
              <Select
                labelId="encounter-mode-label"
                label="Encounter Mode"
                value={formState.encounter_mode}
                onChange={handleFieldChange("encounter_mode")}
                inputProps={{
                  'aria-label': 'Encounter Mode',
                  'data-field': 'encounter_mode'
                }}
              >
                {ENCOUNTER_MODES.map((mode) => (
                  <MenuItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Program Minutes — one field per selected program */}
          {formState.program_types.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: "block", fontSize: "0.7rem", letterSpacing: 0.5 }}>
                ENCOUNTER MINUTES BY PROGRAM
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block", fontSize: "0.65rem" }}>
                Total call time: {Math.floor(callElapsedSeconds / 60)}m {callElapsedSeconds % 60}s
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {formState.program_types.map((prog) => (
                  <TextField
                    key={prog}
                    label={`${prog} mins`}
                    type="number"
                    size="small"
                    value={(formState.program_minutes || {})[prog] ?? ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormState((prev) => ({
                        ...prev,
                        program_minutes: { ...(prev.program_minutes || {}), [prog]: val === "" ? "" : Number(val) },
                      }));
                    }}
                    inputProps={{ min: 0, max: 120, "aria-label": `${prog} minutes`, style: { textAlign: "center" } }}
                    sx={{ width: 90 }}
                  />
                ))}
                <Tooltip title="Auto-fill: split call time evenly across selected programs" placement="top" arrow>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const totalMins = Math.round(callElapsedSeconds / 60);
                      const count = formState.program_types.length;
                      const perProgram = Math.floor(totalMins / count);
                      const remainder = totalMins % count;
                      setFormState((prev) => {
                        const mins = {};
                        prev.program_types.forEach((p, i) => {
                          mins[p] = perProgram + (i === 0 ? remainder : 0);
                        });
                        return { ...prev, program_minutes: mins };
                      });
                    }}
                    sx={{ whiteSpace: "nowrap", minWidth: "auto", fontSize: "0.7rem" }}
                  >
                    Auto-fill
                  </Button>
                </Tooltip>
              </Stack>
            </Box>
          )}

          <TextField
            label="Encounter focus"
            placeholder="Example: Monthly CCM touchpoint, medication refill, symptom triage"
            value={formState.encounter_focus}
            onChange={handleFieldChange("encounter_focus")}
            fullWidth
            sx={{ mb: 0.5 }}
            inputProps={{
              'aria-label': 'Encounter focus',
              'data-field': 'encounter_focus'
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocalPhoneIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />

          {chartActivityLog.length > 0 && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: "1px solid rgba(76, 175, 80, 0.2)",
                backgroundColor: "rgba(76, 175, 80, 0.04)",
                mb: 0.5,
              }}
            >
              <Typography variant="caption" fontWeight={600} color="success.main" display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <CheckCircleOutlineIcon fontSize="small" />
                Chart activity this encounter ({chartActivityLog.length})
              </Typography>
              {chartActivityLog.map((entry, i) => (
                <Typography key={i} variant="caption" display="block" color="text.secondary" sx={{ pl: 1 }}>
                  ✓ {entry.text}
                </Typography>
              ))}
            </Box>
          )}
          <Stack direction="row" spacing={1}>
            <Tooltip title="Generate a factual summary of the patient's current chart. Only reflects what actually exists — no implied actions.">
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={() => handleGenerateDocumentation("summary")}
                disabled={generating || saving}
                startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                aria-label="Generate patient summary"
                data-action="generate_ai_docs"
                sx={{
                  py: 1.25,
                  borderStyle: "dashed",
                  borderColor: "secondary.main",
                  "&:hover": {
                    borderStyle: "solid",
                    backgroundColor: "rgba(156, 39, 176, 0.08)",
                  },
                }}
              >
                {generating ? "Generating…" : "✨ Generate Notes"}
              </Button>
            </Tooltip>
            <Tooltip title={chartActivityLog.length > 0 ? "Update existing notes with chart changes made this session" : "No session activity yet — make changes to the chart first"}>
              <span>
                <Button
                  variant="outlined"
                  color="info"
                  onClick={() => handleGenerateDocumentation("update")}
                  disabled={generating || saving || chartActivityLog.length === 0}
                  startIcon={<RefreshIcon />}
                  aria-label="Update notes with session activity"
                  data-action="refresh_notes"
                  sx={{
                    py: 1.25,
                    minWidth: 140,
                    borderStyle: chartActivityLog.length > 0 ? "solid" : "dashed",
                    "&:hover": { borderStyle: "solid" },
                  }}
                >
                  Update Notes
                </Button>
              </span>
            </Tooltip>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 0.5 }}>
            Generate: patient chart summary · Update Notes: adds this session's activity to existing notes
          </Typography>

          <TextField
            label="Call summary"
            value={formState.summary}
            onChange={handleFieldChange("summary")}
            multiline
            minRows={4}
            placeholder="What happened on this call? What was discussed, completed, any patient concerns?"
            sx={{ mb: 0.5 }}
            inputProps={{
              'aria-label': 'Call summary',
              'data-field': 'summary'
            }}
          />

          <TextField
            label="Coordinator call notes"
            value={formState.notes}
            onChange={handleFieldChange("notes")}
            multiline
            minRows={4}
            placeholder="What you did: assessments completed, vitals reviewed, barriers discussed, coaching given, escalations, device/enrollment updates…"
            sx={{ mb: 0.5 }}
            inputProps={{
              'aria-label': 'Coordinator call notes',
              'data-field': 'notes'
            }}
          />

          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: "1px solid rgba(255,255,255,0.06)",
              backgroundColor: "rgba(255,255,255,0.01)",
            }}
          >
            <Typography variant="subtitle2" gutterBottom display="flex" alignItems="center" gap={0.5} mb={2}>
              <TaskAltIcon fontSize="small" color="info" />
              Interventions completed
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {INTERVENTION_OPTIONS.map((option) => (
                <Chip
                  key={option}
                  label={option}
                  variant={formState.interventions.includes(option) ? "filled" : "outlined"}
                  color={formState.interventions.includes(option) ? "primary" : "default"}
                  onClick={() => toggleChipValue("interventions", option)}
                  data-intervention={option}
                  aria-label={`Toggle ${option}`}
                />
              ))}
            </Stack>
          </Box>

          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: "1px solid rgba(255,255,255,0.06)",
              backgroundColor: "rgba(255,255,255,0.01)",
            }}
          >
            <Typography variant="subtitle2" gutterBottom display="flex" alignItems="center" gap={0.5} mb={2}>
              <TrackChangesIcon fontSize="small" color="secondary" />
              Next steps & follow-up
            </Typography>
            <TextField
              label="Next actions (one per line)"
              value={formState.next_steps_text}
              onChange={handleFieldChange("next_steps_text")}
              multiline
              minRows={3}
              placeholder="Schedule lab review\nShip BP cuff\nReinforce fall precautions"
              inputProps={{
                'aria-label': 'Next action steps',
                'data-field': 'next_steps'
              }}
            />
            <TextField
              label="Follow-up date"
              type="datetime-local"
              value={formState.follow_up_at}
              onChange={handleFieldChange("follow_up_at")}
              fullWidth
              sx={{ mt: 2 }}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                'aria-label': 'Follow-up date and time',
                'data-field': 'follow_up_at'
              }}
            />
          </Box>

          {/* Coordinator Next Steps — carries to next call's P0 */}
          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: "1px solid rgba(156, 39, 176, 0.2)",
              backgroundColor: "rgba(156, 39, 176, 0.04)",
            }}
          >
            <Typography variant="subtitle2" gutterBottom display="flex" alignItems="center" gap={0.5} mb={2}>
              <AssignmentTurnedInIcon fontSize="small" color="secondary" />
              Coordinator next steps
              <Chip label="→ feeds next call P0" size="small" variant="outlined" color="secondary" sx={{ ml: 1, fontSize: 10, height: 20 }} />
            </Typography>
            <TextField
              label="Internal coordinator actions (one per line)"
              value={formState.coordinator_next_steps_text}
              onChange={handleFieldChange("coordinator_next_steps_text")}
              multiline
              minRows={2}
              placeholder="Review lab results before next call\nCoordinate with PCP for med adjustment\nFollow up on transportation referral"
              fullWidth
              inputProps={{
                'aria-label': 'Coordinator next steps',
                'data-field': 'coordinator_next_steps'
              }}
            />
          </Box>

          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: "1px solid rgba(255,255,255,0.06)",
              backgroundColor: "rgba(255,255,255,0.01)",
            }}
          >
            <Typography variant="subtitle2" gutterBottom display="flex" alignItems="center" gap={0.5} mb={2}>
              <PsychologyIcon fontSize="small" color="warning" />
              Patient context
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="patient-mood-label">Patient Mood</InputLabel>
              <Select
                labelId="patient-mood-label"
                value={formState.patient_mood}
                label="Patient Mood"
                onChange={handleFieldChange("patient_mood")}
                inputProps={{
                  'aria-label': 'Patient mood',
                  'data-field': 'patient_mood'
                }}
              >
                {MOOD_OPTIONS.map((mood) => (
                  <MenuItem key={mood} value={mood}>
                    {mood}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2} mb={1.5}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={formState.vitals_reviewed} 
                    onChange={handleFieldChange("vitals_reviewed")}
                    inputProps={{
                      'aria-label': 'Vitals reviewed',
                      'data-field': 'vitals_reviewed'
                    }}
                  />
                }
                label="Vitals reviewed"
              />
              <FormControlLabel
                control={
                  <Switch 
                    checked={formState.medications_reviewed} 
                    onChange={handleFieldChange("medications_reviewed")}
                    inputProps={{
                      'aria-label': 'Medications reviewed',
                      'data-field': 'medications_reviewed'
                    }}
                  />
                }
                label="Medications reviewed"
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={formState.barriers_reviewed} 
                    onChange={handleFieldChange("barriers_reviewed")}
                    inputProps={{
                      'aria-label': 'Barriers addressed',
                      'data-field': 'barriers_reviewed'
                    }}
                  />
                }
                label="Barriers addressed"
              />
              <FormControlLabel
                control={
                  <Switch 
                    checked={formState.escalation_required} 
                    onChange={handleFieldChange("escalation_required")}
                    inputProps={{
                      'aria-label': 'Escalation needed',
                      'data-field': 'escalation_required'
                    }}
                  />
                }
                label="Escalation needed"
              />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Resources provided
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {RESOURCE_OPTIONS.map((option) => (
                <Chip
                  key={option}
                  label={option}
                  variant={formState.resources_offered.includes(option) ? "filled" : "outlined"}
                  color={formState.resources_offered.includes(option) ? "success" : "default"}
                  onClick={() => toggleChipValue("resources_offered", option)}
                  data-resource={option}
                  aria-label={`Toggle ${option} resource`}
                />
              ))}
            </Stack>
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between" mt="auto" pt={2} gap={2}>
            <Tooltip title="Save timer + documentation without completing the encounter">
              <span style={{ flex: 1 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  fullWidth
                  onClick={() => handleSave()}
                  disabled={saving}
                  aria-label="Save encounter draft"
                  data-action="save_draft"
                >
                  Save Draft
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={handleComplete}
              disabled={saving || !formState.summary}
              aria-label="Complete encounter"
              data-action="complete_encounter"
            >
              Complete Encounter
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}

EncounterDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patient: PropTypes.object,
  session: PropTypes.object,
  apiBase: PropTypes.string.isRequired,
  loadingSession: PropTypes.bool,
  onSessionChange: PropTypes.func,
  onShowSnackbar: PropTypes.func,
  onEncounterCompleted: PropTypes.func,
  agendaData: PropTypes.object,
  chartActivityLog: PropTypes.array,
};

export default EncounterDrawer;
